import { useFormContext } from '@calendly/form'
import { useCallback, useEffect, useRef } from 'react'

const fieldOwnsPath = (fieldName, path) => (
  path === fieldName || path.startsWith(`${fieldName}.`) || path.startsWith(`${fieldName}[`)
)

export const pathBelongsToFields = (path, fieldNames) => (
  path && fieldNames.some((fieldName) => fieldOwnsPath(fieldName, path))
)

const areFieldNamesEqual = (fieldNames, nextFieldNames) => (
  fieldNames.length === nextFieldNames.length
    && fieldNames.every((fieldName, index) => fieldName === nextFieldNames[index])
)

const getErrorsByPath = (error) => {
  const validationErrors = error.inner?.length ? error.inner : [error]

  return validationErrors.reduce((errorsByPath, validationError) => {
    if (!validationError.path || errorsByPath[validationError.path]) return errorsByPath

    return {
      ...errorsByPath,
      [validationError.path]: validationError.message
    }
  }, {})
}

export const useActiveTabValidation = ({
  activeTabKey,
  fieldNames,
  validationSchema
}) => {
  const { setFieldError, values } = useFormContext()
  const previousValuesRef = useRef(values)
  const previousScopeRef = useRef({ activeTabKey, fieldNames })
  const validationRunRef = useRef(0)
  const activeErrorPathsRef = useRef(new Set())

  const clearActiveErrors = useCallback((topLevelNames) => {
    topLevelNames.forEach((fieldName) => setFieldError(fieldName, undefined))
    activeErrorPathsRef.current.forEach((path) => setFieldError(path, undefined))
    activeErrorPathsRef.current = new Set()
  }, [setFieldError])

  useEffect(() => {
    const previousScope = previousScopeRef.current
    const scopeChanged = previousScope.activeTabKey !== activeTabKey
      || !areFieldNamesEqual(previousScope.fieldNames, fieldNames)

    if (scopeChanged) {
      clearActiveErrors(previousScope.fieldNames)
      previousScopeRef.current = { activeTabKey, fieldNames: [...fieldNames] }
    }

    if (previousValuesRef.current === values) return
    previousValuesRef.current = values

    if (!validationSchema || !fieldNames.length) return

    const validationRun = validationRunRef.current + 1
    validationRunRef.current = validationRun

    validationSchema.validate(values, { abortEarly: false })
      .then(() => {
        if (validationRunRef.current !== validationRun) return

        clearActiveErrors(fieldNames)
      })
      .catch((error) => {
        if (validationRunRef.current !== validationRun) return

        clearActiveErrors(fieldNames)
        Object.entries(getErrorsByPath(error)).forEach(([path, message]) => {
          if (pathBelongsToFields(path, fieldNames)) {
            setFieldError(path, message)
            activeErrorPathsRef.current.add(path)
          }
        })
      })
  }, [activeTabKey, clearActiveErrors, fieldNames, setFieldError, validationSchema, values])
}
